import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";
import api from "@/lib/api";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the MusicFlow StudioAi assistant. Ask me anything about the API, adaptive soundscapes, credits or the SDKs." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState("");
  const [provider, setProvider] = useState("claude");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing, open]);

  const typeOut = (full) => new Promise((resolve) => {
    const words = full.split(" ");
    let i = 0;
    setTyping("");
    const step = () => {
      i += 1;
      setTyping(words.slice(0, i).join(" "));
      if (i < words.length) setTimeout(step, 28);
      else { setTyping(""); resolve(); }
    };
    step();
  });

  const send = async () => {
    const text = input.trim();
    if (!text || loading || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/support/chat", { message: text, session_id: sessionId, provider });
      setSessionId(data.session_id);
      setLoading(false);
      await typeOut(data.reply);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setLoading(false);
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the assistant. Please try again." }]);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center glow-primary hover:scale-105 transition-transform"
        data-testid="support-chat-toggle"
        aria-label="Support chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden" data-testid="support-chat-panel">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/60">
            <Bot size={18} className="text-primary" />
            <div className="font-display font-bold text-sm">MusicFlow StudioAi Assistant</div>
            <span className="ml-auto text-[10px] font-mono text-muted-foreground">AI</span>
            <button onClick={() => setProvider((p) => (p === "claude" ? "openai" : "claude"))} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid="support-chat-provider" title="Switch model">
              {provider === "claude" ? "Claude" : "ChatGPT"}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-background border border-border rounded-bl-sm"}`} data-testid={`chat-msg-${m.role}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2 bg-background border border-border"><Loader2 className="animate-spin text-primary" size={16} /></div></div>
            )}
            {typing && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm whitespace-pre-wrap bg-background border border-border">
                  {typing}<span className="inline-block w-1.5 h-4 ml-0.5 bg-primary/70 align-middle animate-pulse" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-border flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about the API, credits, SDKs…"
              className="flex-1 bg-background border border-border rounded-full h-10 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="support-chat-input"
            />
            <button onClick={send} disabled={loading || !!typing || !input.trim()} className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50" data-testid="support-chat-send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
